/* eslint-disable no-console */
import { ReactNode, useCallback, useEffect } from 'react';
import StackTrace from 'stacktrace-js';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { ApiLogLevel } from '@agunity/api-v4';

import AgTypography from '../../stories/Styles/AgTypography';
import AgButton from '../../stories/Components/AgButton';
import { AgColor } from '../../stories/Styles/AgColor';

import { capitalizeFirstLetter } from '../../utils';
import { appEnv, isDev } from '../../api/environments';
import { loggingUIError } from './redux';
import { selectUserToken } from '../../redux/slices/auth';
import { setIsOpenSupportModal } from '../../redux/slices/member';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectVersion, setApiErrorMessage, setErrorMessage } from '../../redux/slices/global';
import packageInfo from '../../../package.json';
import * as S from './styled';

type AgErrorBoundary = { children: ReactNode };
type ErrorBoundaryUIFallback = { error: Error; resetErrorBoundary: () => void };

const ErrorBoundaryUIFallback = ({ error, resetErrorBoundary }: ErrorBoundaryUIFallback) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectUserToken);
  const { environmentName = '', assemblyVersion = '0.0.0' } = useAppSelector(selectVersion) || {};
  
  // Clear stack/prompt/alert error state
  const handleClear = useCallback(() => {
    dispatch(setErrorMessage(null));
    dispatch(setApiErrorMessage(null));
  }, [dispatch]);

  // Reset the error boundary to clear any existing error state
  const handleRefresh = useCallback( () => {
    resetErrorBoundary();
  }, [resetErrorBoundary]);

  // Navigate to the home page ('/')
  const handleGoToHome = useCallback(() => {
    if (location?.pathname === '/') {
      handleRefresh();

      // If the URL contains hash (#...) or search (?...) parameters, clean them up
      if (window.location.hash || window.location.search) {
        navigate({ pathname: '/', search: '', hash: '' }, { replace: true });
      }
    } else {
      // If not on the home page, navigate to the home page
      navigate('/', { replace: true });
    }
  }, [handleRefresh, location?.pathname, navigate]);

  // Show support modal
  const handleShowSupportModal = () => {
    dispatch(setIsOpenSupportModal(true));
  };

  const logUIError = useCallback(async () => {
    try {
      if (isDev || !error) return;

      // Format the stack trace into a readable string
      const stackFrames = await StackTrace.fromError(error);
      const formattedStack = stackFrames
        .map((frame) => {
          const fileName = frame.getFileName();

          // Filter out frames that are not TypeScript or JSX files
          if (!/[\\/].*\.(ts|tsx|jsx)$/.test(fileName)) return null;
          return `${frame.getFunctionName() || '<anonymous>'} (${fileName}:${frame.getLineNumber()}:${frame.getColumnNumber()})`;
        })
        .filter(Boolean)
        .join('\n');
  
      const errorMessage = (error.message || '').trim() || 'An unknown error occurred';
      const stackTrace = [errorMessage, formattedStack].join('\n');
      console.error('[Stack Trace]:\n', stackTrace);
  
      const payload = {
        level: ApiLogLevel.Critical,
        userToken: token,
        target: 'Hub',
        url: window?.location?.href || '',
        stackTrace,
        apiVersion: `${environmentName} ${assemblyVersion}`,
        uiVersion: capitalizeFirstLetter(`${appEnv} ${packageInfo.version}`),
      };

      await dispatch(loggingUIError(payload));
    } catch (err) {
      console.error('Error logging UI error:', err);
    }
  }, [assemblyVersion, dispatch, environmentName, error, token]);

  useEffect(() => {
    handleClear();
    const debounce = setTimeout(() => logUIError(), 500);
    return () => clearTimeout(debounce);
  }, [handleClear, logUIError]);

  return (
    <S.Root>
      <S.Container>
        <AgTypography component='h3' contentStyle='AP.H3.Medium' style={{ color: AgColor.errorMain }}>
          <FormattedMessage id='ERROR_BOUNDARY_ERROR'/>
        </AgTypography>

        <AgTypography component='h2' contentStyle='AP.H2.Bold' style={{ color: AgColor.primaryMain }}>
          <FormattedMessage id='ERROR_BOUNDARY_TITLE' />
        </AgTypography>

        <AgTypography component='p' contentStyle='AP.Paragraph.Large.Regular' style={{ color: AgColor.grey9 }}>
          <FormattedMessage id='ERROR_BOUNDARY_DESC_1' />
        </AgTypography>

        <AgTypography component='p' contentStyle='AP.Paragraph.Large.Regular' style={{ color: AgColor.grey9 }}>
          <FormattedMessage id='ERROR_BOUNDARY_DESC_2' values={{ link: (
            <AgTypography component='span' contentStyle='AP.Paragraph.Large.Link' onClick={handleShowSupportModal} style={{ cursor: 'pointer' }}>
              <FormattedMessage id='ERROR_BOUNDARY_LINK' />
            </AgTypography>
          ) }}/>
        </AgTypography>

        {/* Action Buttons */}
        <Stack direction='row' gap={2}>
          <AgButton label={formatMessage({ id: 'HOME' })} onClick={handleGoToHome} />
          <AgButton label={formatMessage({ id: 'REFRESH' })} variant='outlined' onClick={handleRefresh} />
        </Stack>
      </S.Container>
    </S.Root>
  );
};

const AgErrorBoundary = ({ children }: AgErrorBoundary) => {
  const location = useLocation();

  return (
    <ErrorBoundary
      FallbackComponent={(props) => <ErrorBoundaryUIFallback {...props} />}
      resetKeys={[location.pathname]} 
    >
      {children}
    </ErrorBoundary>
  );
};

export default AgErrorBoundary;